import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
} from '@material-ui/core';

import { showToast } from '@klpkonsern/ui';
import { Block } from '@klp/design';
import { ActionButton } from '@klp/design/ActionButton';
import { getDefaultLanguage } from '@klp/layout/Sidebar/config';

import { useSendWelcomeMessageMutation } from '../rtkApi';

interface ISendWelcomeMessageProps {
    email: string;
    isInvitation?: boolean;
    onSuccess: () => void;
}

export const SendWelcomeMessage: React.FC<ISendWelcomeMessageProps> = ({
    email,
    isInvitation,
    onSuccess,
}) => {
    const { t } = useTranslation();
    const [displayDialog, setDisplayDialog] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [sendWelcomeMessage] = useSendWelcomeMessageMutation();

    const handleSendWelcomeMessageClicked = () => {
        setDisplayDialog(true);
    };

    const handleSendWelcomeMessageClose = () => {
        if (!isLoading) {
            setDisplayDialog(false);
        }
    };

    const handleSendWelcomeMessageConfirmed = async () => {
        const culture = getDefaultLanguage();
        if (email) {
            setIsLoading(true);
            try {
                await sendWelcomeMessage({
                    email,
                    culture,
                }).unwrap();
                onSuccess();
                setDisplayDialog(false);
                showToast({
                    body: t('userSearch.sendWelcomeMessageSuccess', { email }),
                    type: 'success',
                });
            } catch (error) {
                showToast({
                    body: t('userSearch.sendWelcomeMessageError', { email }),
                    type: 'error',
                });
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <>
            <Block>
                <ActionButton
                    actionType="forward"
                    translation={
                        isInvitation ? 'userSearch.invite' : 'userSearch.send'
                    }
                    clickHandler={handleSendWelcomeMessageClicked}
                    klpTypographyVariant="small"
                />
            </Block>
            <Dialog
                open={displayDialog}
                onClose={handleSendWelcomeMessageClose}
            >
                <DialogContent>
                    <DialogContentText>
                        {isInvitation
                            ? t('userSearch.inviteUserQuestion', { email })
                            : t('userSearch.sendWelcomeMessageQuestion', {
                                  email,
                              })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <ActionButton
                        actionType="cancel"
                        onClick={handleSendWelcomeMessageClose}
                        translation="general.decline"
                        disabled={isLoading}
                    />
                    <ActionButton
                        actionType="forward"
                        translation="general.confirm"
                        onClick={handleSendWelcomeMessageConfirmed}
                        isLoading={isLoading}
                    />
                </DialogActions>
            </Dialog>
        </>
    );
};
